import { Stack, useMantineTheme } from '@mantine/core';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ISegmentDefinition, MailrailAutoSendEmailCampaign } from '@feathr/blackbox';
import { getSegmentsUniqueEmailsPayload, transformTriggersToPredicates } from '@feathr/blackbox';
import { Alert, Checkbox, FormElement } from '@feathr/components';
import { useUniqueEmailsCount } from '@feathr/extender/hooks';
import { useId } from '@feathr/hooks';

interface IProps {
  campaign: MailrailAutoSendEmailCampaign;
  disabled: boolean;
}

function MailrailBulkSendConfig({ campaign, disabled }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const labelId = useId();
  const bulkSendAtStart = campaign.get('bulk_send_at_start');
  const bulkSendAtEnd = campaign.get('bulk_send_at_end');
  // This rule exists mainly to avoid the frontend validation complexity of enforcing "a
  // campaign that performed an end-of-campaign bulk send cannot be edited or re-published"
  // while allowing users to set bulk_send_at_end=True on a completed campaign without
  // instantly locking it. There's no technical reason an end-time bulk send can't be added
  // to a campaign midway or upon re-publishing. Might be worth a follow-up enhancement.
  const canConfigureEndBulkSend = campaign.isDraft || campaign.isScheduled;
  const includedSegmentIds = campaign.get('segments_included', []).filter(Boolean);

  const previouslyMatchedCount = useUniqueEmailsCount(
    (Segments) => {
      const predicates = campaign.get('predicates');
      if (!predicates.length) {
        return null;
      }
      const predicatesMode = campaign.get('predicates_mode') ?? 'match_any';
      let segmentPredicates = transformTriggersToPredicates(toJS(predicates));
      // Activity predicates must be grouped so that a person matches only when a single
      // breadcrumb satisfies all of them — the same grouping the backend applies when it
      // builds the start-send audience (predicates_to_segment).
      if (campaign.get('subtype') === 'activity') {
        segmentPredicates = [
          { group: segmentPredicates, group_mode: predicatesMode, kind: 'activity' },
        ];
      }
      // An ephemeral segment filtering the target audience to persons who already satisfied the
      // campaign's trigger predicates
      const triggerPredicateSegment: ISegmentDefinition = {
        lookback_mode: 'unbounded',
        mode: predicatesMode,
        predicates: segmentPredicates,
      };
      const payload = getSegmentsUniqueEmailsPayload(Segments, campaign);
      return {
        ...payload,
        // The trigger filter narrows the included groups when there are any; with no included
        // groups it is itself the audience.
        ...(payload.included_segment_ids?.length || payload.included_segments?.length
          ? { filter_segment: triggerPredicateSegment }
          : { included_segments: [triggerPredicateSegment] }),
      };
    },
    [campaign],
  );

  function handleChangeStart(newValue?: boolean): void {
    campaign.set({ bulk_send_at_start: !!newValue });
  }

  function handleChangeEnd(newValue?: boolean): void {
    campaign.set({ bulk_send_at_end: !!newValue });
  }

  function getGroupHelpText(): string {
    if (campaign.isMailrailDrip) {
      return t(
        'By default, no bulk enrollment is included. You can optionally add one at the start of ' +
          'the campaign.',
      );
    }
    return t(
      'By default, no bulk send is included. You can optionally add one at the start of the ' +
        'campaign, the end, or both.',
    );
  }

  function getStartLabel(): string {
    // The em-dash is the shared unknown-value convention: the count is undefined while the
    // request is in flight and after a failed request, and a hard 0 would misread as "no one".
    const countDisplay = previouslyMatchedCount?.toLocaleString() ?? '—';
    if (campaign.isMailrailDrip) {
      return t(
        'At the start, enroll the {{ countDisplay }} eligible recipients who have already matched the selected triggers',
        { countDisplay },
      );
    }
    return t(
      'At the start, email the {{ countDisplay }} eligible recipients who have already matched the selected triggers',
      { countDisplay },
    );
  }

  // A zero or unknown match count doesn't block the start send: recipients may still match the
  // triggers before a scheduled campaign starts, so the send is configurable regardless.
  function getStartDisabledReason(): string | undefined {
    if (campaign.hasStarted) {
      return bulkSendAtStart
        ? t('The campaign already performed this bulk send. It cannot be undone.')
        : t('This option is unavailable because the campaign has already started.');
    }
    return undefined;
  }

  function getEndDisabledReason(): string | undefined {
    if (!canConfigureEndBulkSend) {
      return t('An end-of-campaign bulk send can only be added before the campaign starts.');
    }
    if (!includedSegmentIds.length && campaign.get('repeat_allowed')) {
      return t(
        'This option is unavailable because it requires at least one included group and cannot be combined with the "Send every time" cadence.',
      );
    }
    if (!includedSegmentIds.length) {
      return t('This option is unavailable because it requires at least one included group.');
    }
    if (campaign.get('repeat_allowed')) {
      return t(
        'This option is unavailable because it cannot be combined with the "Send every time" cadence.',
      );
    }
    return undefined;
  }

  const startDisabledReason = getStartDisabledReason();
  const endDisabledReason = getEndDisabledReason();

  return (
    <>
      <FormElement
        aria-labelledby={labelId}
        data-name={'bulk_send_options'}
        helpText={getGroupHelpText()}
        label={campaign.isMailrailDrip ? t('Bulk enrollment') : t('Bulk sends')}
        labelId={labelId}
        role={'group'}
      >
        <Stack gap={theme.spacing['2']}>
          <Checkbox
            disabled={disabled || !!startDisabledReason}
            label={getStartLabel()}
            layout={'block'}
            onChange={handleChangeStart}
            tooltip={
              startDisabledReason ||
              t(
                'The number of matching recipients may change before the start of the campaign, as ' +
                  'a result of incoming activities, contact data updates, and/or group filter edits.',
              )
            }
            value={bulkSendAtStart}
          />
          {!campaign.isMailrailDrip && (
            <Checkbox
              disabled={disabled || !!endDisabledReason}
              label={t(
                'At the end, email ALL eligible recipients who have not yet received the email',
              )}
              layout={'block'}
              onChange={handleChangeEnd}
              tooltip={endDisabledReason}
              value={bulkSendAtEnd}
            />
          )}
        </Stack>
      </FormElement>
      {bulkSendAtEnd && (
        <Alert
          description={t(
            'If you include an end-of-campaign bulk send, you will not be able to re-publish your campaign after it reaches its end date.',
          )}
          title={t('End-of-campaign bulk send is final')}
          type={'info'}
        />
      )}
    </>
  );
}

export default observer(MailrailBulkSendConfig);
